import BigNumber from 'bignumber.js';
import dayjs from 'dayjs';
import api from '../api';
import { useFullListRequest, useRequestImmer } from '../api/hooks';
import { EditorInfo, GMTInfo, Page, PagedDataList } from './common';

export const CouponEndpoint = `/coupon`;

export enum CouponStatus {
  NORMAL = 0,
  OUT = 1,
}

export enum CouponUserStatus {
  /**可用 */
  STATUS_USABLE = 0,
  /**已使用 */
  STATUS_USED = 1,
  /**已过期 */
  STATUS_EXPIRED = 2,
  /**未开始 */
  STATUS_NOT_START = 3,
  /**金额不满足 */
  STATUS_NOT_MATCH_AMOUNT = 4,
}

export const CouponUserStatusDescMap = {
  [CouponUserStatus.STATUS_USABLE]: '可使用',
  [CouponUserStatus.STATUS_USED]: '已使用',
  [CouponUserStatus.STATUS_EXPIRED]: '已过期',
  [CouponUserStatus.STATUS_NOT_START]: '未到可用时间',
  [CouponUserStatus.STATUS_NOT_MATCH_AMOUNT]: '商品总额不足',
};

export const getCouponStatusDesc = (status: CouponUserStatus) => {
  return CouponUserStatusDescMap[status];
};

export const CouponTypesEnum = {
  DISCOUNT: 1,
  REDUCE: 2,
};

const couponTypes = {
  1: '折扣券',
  2: '满减券',
  3: '抵用券',
};

export const getCouponTypeDesc = (code: Coupon['couponType']) => couponTypes[code];

/**
 * 计算优惠券抵扣金额
 * @param coupon
 * @param originalPrice
 */
export const getCouponPrice = (coupon: Coupon, originalPrice: BigNumber) => {
  const couponPrice = new BigNumber(0);

  const couponType = coupon.couponType;
  if (couponType === CouponTypesEnum.DISCOUNT) {
    // 打折
    return originalPrice.minus(
      originalPrice.multipliedBy(new BigNumber(coupon.discount).multipliedBy(0.01))
    );
  } else if (couponType === CouponTypesEnum.REDUCE) {
    // 减免
    return new BigNumber(coupon.discount);
  }
  return couponPrice;
};

export const convertRate = (rate: number) => {
  // if (rate % 10 === 0) {
  //   return rate / 10;
  // }
  return rate / 10;
};

export type CouponType = keyof typeof couponTypes;

/** Coupon */
export interface Coupon extends GMTInfo, EditorInfo {
  id: number;

  cid: number;

  userId: number;

  /**名称 */
  name: string;

  /**发放数量 */
  total: number;

  /** 类型，0: 通用券，用户领取；1: 注册赠券；2: 优惠券码兑换；*/
  type: 0 | 1 | 2;

  /**类型: 1:折扣券; 2:满减券; 3:抵用券 */
  // couponType: 1 | 2 | 3;
  couponType: CouponType;

  /**使用金额门槛 */
  min: number;

  /**优惠额度 */
  discount: number;

  /**状态*/
  status: CouponStatus | CouponUserStatus;

  /**领取开始时间 */
  produceStartTime?: string | dayjs.Dayjs;

  /**领取结束时间 */
  produceEndTime?: string | dayjs.Dayjs;

  /**领取有效期区间 */
  produceTimeRange?: [dayjs.Dayjs, dayjs.Dayjs];

  /**使用开始时间 */
  consumeStartTime?: string | dayjs.Dayjs;

  /**使用结束时间 */
  consumeEndTime?: string | dayjs.Dayjs;

  /**使用有效期区间 */
  consumeTimeRange?: [dayjs.Dayjs, dayjs.Dayjs];

  /**有效时间限制，0: 领取时间的有效天数days；1，则 consumeStartTime 和 consumeEndTime 是优惠券有效期；*/
  timeType: 0 | 1;

  // 发券渠道 1 经营端; 2 小程序
  distributeChannel: 1 | 2;

  /** 基于领取时间的有效天数days */
  days: number;

  desc: string;

  /**核销码 */
  writeOffCode: string;

  receiveTime: string;

  sortOrder: number;

  available: boolean;
}

export interface CouponStatistic {
  writeOffNum: number;
  receivedNum: number;
  expiredNum: number;
}

const fetchCouponList = async () => {
  return await api.get<{
    list: Coupon[];
    page: Page;
  }>({
    url: `${CouponEndpoint}`,
  });
};

/**
 * 向用户发券
 * @param param0
 * @returns
 */
export const grantCouponUser = async ({
  username,
  couponId,
}: {
  username: string;
  couponId: number;
}) => {
  return await api.post({
    url: `${CouponEndpoint}/grant/${username}`,
    data: { id: couponId },
  });
};

/**
 * 获取所有优惠券
 * @returns
 */
export const useFetchCouponList = () => {
  const { ...rest } = useFullListRequest<Coupon>(
    {
      method: 'get',
      url: `${CouponEndpoint}`,
    },
    {
      manual: true,
    }
  );
  return { ...rest };

  // const { send, ...rest } = useRequest<{
  //   list: Coupon[];
  //   page: Page;
  // }>(
  //   {
  //     method: 'get',
  //     url: `${CouponEndpoint}`,
  //   },
  //   {
  //     manual: true,
  //   }
  // );

  // const [status, setStatus] = useImmer({ loading: false, loaded: false, payload: [] as Coupon[] });

  // const fetch = async () => {
  //   setStatus(draft => {
  //     draft.loading = true;
  //   });

  //   let result = [] as Coupon[];
  //   let current = 1;

  //   while (true) {
  //     const rsp = await send({ params: { page: current++, limit: 2 } });
  //     result = result.concat(rsp.list);
  //     if (!rsp.page || rsp.page.current === rsp.page.pages) {
  //       break;
  //     }
  //   }

  //   setStatus(draft => {
  //     draft.loading = false;
  //     draft.loaded = true;
  //     draft.payload = result;
  //   });
  //   return result;
  // };

  // return {
  //   ...rest,
  //   loading: status.loading,
  //   loaded: status.loaded,
  //   payload: status.payload,
  //   send: fetch,
  //   setPayload: (updator: (draft: Coupon[]) => void | Coupon[]) => {
  //     setStatus(draft => {
  //       const newState = updator(draft.payload);
  //       if (newState) {
  //         draft.payload = newState;
  //       }
  //     });
  //   },
  // };
};

export const useFetchCouponUserList = () => {
  const { send, ...rest } = useRequestImmer<PagedDataList<Coupon>>(
    {
      method: 'get',
      url: `${CouponEndpoint}/user`,
    },
    {
      manual: true,
    }
  );
  return {
    ...rest,
    send: (params: { couponId?: number; page: number; limit: number }) => {
      send({
        params,
      });
    },
  };
};
